import Card from "@/UI/Card";
import Loader from "@/components/Loader/Loader";
import { Form, Input, InputNumber, Select, Button, Modal, message } from "antd";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateLeadMutation } from "@/redux/rtk/features/leads/leadsApi";
import { useUpdateDealMutation } from "@/redux/rtk/features/CRM/deal/dealApi";
import { useGetDealStagesQuery } from "@/redux/rtk/features/CRM/dealStage/dealStageApi";
import { useParams } from "react-router-dom";
import usePermissions from "@/utils/usePermissions";
import { toastHandler } from "@/utils/functions";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { FaWhatsapp } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { loadAllStaff } from "@/redux/rtk/features/hrm/user/userSlice";
import { loadAllAgent } from "@/redux/rtk/features/CRM/agent/agentSlice";
import { updateContact } from "@/redux/rtk/features/CRM/contact/contactSlice";
import { useGetLeadSourcesQuery } from "@/redux/rtk/features/leadSource/leadSourceApi";
import { useGetInterestsQuery } from "@/redux/rtk/features/interest/interestApi";
import { useGetMajorsQuery } from "@/redux/rtk/features/major/majorApi";
import { useGetUniversitiesQuery } from "@/redux/rtk/features/university/universityApi";
import { useGetDegreesQuery } from "@/redux/rtk/features/degree/degreeApi";
import { useGetSemestersQuery, useAddSemesterMutation } from "@/redux/rtk/features/semesterRegistered/semesterRegisteredApi";
import { PlusOutlined } from "@ant-design/icons";
import { NATIONALITIES } from "@/utils/nationalities";
import { COUNTRIES } from "@/utils/countries";
import { getSetting } from "@/redux/rtk/features/setting/settingSlice";

const { Option } = Select;
const { TextArea } = Input;

const getId = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  let id = value;
  if (typeof value === 'object' && value !== null && value.id !== undefined) {
    id = value.id;
  }
  const num = parseInt(id);
  return isNaN(num) ? String(id) : num;
};

// Helper function to get array of IDs from array (handles both IDs and objects)
const getIdsArray = (value) => {
  if (!value || !Array.isArray(value)) return [];
  return value.map(item => {
    let id = item;
    if (typeof item === 'object' && item !== null && item.id !== undefined) {
      id = item.id;
    }
    const num = parseInt(id);
    return id !== null && id !== undefined ? (isNaN(num) ? String(id) : num) : null;
  }).filter(id => id !== null);
};

export default function DealInformation({ data, loading, contactStatuses, onUpdate }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const userId = localStorage.getItem("id");
  const userName = localStorage.getItem("user");
  const watchedUniversityIds = Form.useWatch("universityIds", form) || [];
  const watchedContactMobile = Form.useWatch("contactMobile", form);
  const watchedContactPhone = Form.useWatch("contactPhone", form);
  const [showDetails, setShowDetails] = useState(false);
  const [updateDeal, { isLoading: isUpdatingDeal }] = useUpdateDealMutation();
  const [updateLead, { isLoading: isUpdatingLead }] = useUpdateLeadMutation();
  const { permissions } = usePermissions();
  const canEdit = permissions?.includes("update-deal");
  const canReadCommission = permissions?.includes("read-commission");
  const [stageModalVisible, setStageModalVisible] = useState(false);
  const [pendingStageId, setPendingStageId] = useState(null);
  const [stageNotes, setStageNotes] = useState("");
  const [originalStageId, setOriginalStageId] = useState(null);

  // Load lists
  const { list: ownerList, loading: ownerLoading } = useSelector((state) => state.users);
  const { list: companyList, loading: companyLoading } = useSelector((state) => state.agent);
  const { data: leadSourcesData, isLoading: leadSourceLoading } = useGetLeadSourcesQuery();
  const leadSources = Array.isArray(leadSourcesData?.leadSources) ? leadSourcesData.leadSources : (Array.isArray(leadSourcesData) ? leadSourcesData : []);

  const { data: interestsData, isLoading: interestLoading } = useGetInterestsQuery();
  const interests = Array.isArray(interestsData?.interests) ? interestsData.interests : (Array.isArray(interestsData) ? interestsData : []);

  const { data: majorsData, isLoading: majorLoading } = useGetMajorsQuery();
  const majors = Array.isArray(majorsData?.majors) ? majorsData.majors : (Array.isArray(majorsData) ? majorsData : []);

  const { data: universitiesData, isLoading: universityLoading } = useGetUniversitiesQuery();
  const universities = Array.isArray(universitiesData?.universities) ? universitiesData.universities : (Array.isArray(universitiesData) ? universitiesData : []);

  const { data: dealStagesData, isLoading: stagesLoading } = useGetDealStagesQuery();
  const { data: degrees, isLoading: degreeLoading } = useGetDegreesQuery();
  const dealStages = Array.isArray(dealStagesData?.dealStages) ? dealStagesData.dealStages : (Array.isArray(dealStagesData) ? dealStagesData : []);

  const { data: semestersData, isLoading: semestersLoading } = useGetSemestersQuery();
  const semesters = Array.isArray(semestersData) ? semestersData : [];
  const [addSemester, { isLoading: isAddingSemester }] = useAddSemesterMutation();
  const [semesterModalOpen, setSemesterModalOpen] = useState(false);
  const [semesterForm] = Form.useForm();

  const { data: setting } = useSelector((state) => state.setting);
  const [fieldConfig, setFieldConfig] = useState({});

  useEffect(() => {
    dispatch(loadAllStaff({ query: "all" }));
    dispatch(loadAllAgent());
    if (!setting) {
      dispatch(getSetting());
    }
  }, [dispatch, setting]);

  useEffect(() => {
    if (setting?.dealFieldConfig) {
      try {
        setFieldConfig(JSON.parse(setting.dealFieldConfig));
      } catch (e) {
        console.error("Error parsing deal field config", e);
      }
    }
  }, [setting]);

  const isRequired = (key) => {
    return fieldConfig[key] === undefined ? true : fieldConfig[key];
  };

  // Country list
  const countries = COUNTRIES;

  // Nationality list
  const nationalities = NATIONALITIES;

  useEffect(() => {
    if (data) {

      // Get lead data - handle both nested (data.lead) and direct (data is lead) structures
      const leadData = data?.lead || data;

      const formValues = {
        // Deal fields
        dealName: data?.dealName ?? "",
        dealOwnerId: getId(data?.dealOwnerId) ?? getId(data?.dealOwner?.id) ?? null,
        contactStatusId: getId(data?.contactStatusId) ?? getId(data?.contactStatus?.id) ?? null,
        scholarship: data?.scholarship !== undefined && data?.scholarship !== null ? data.scholarship : 0,
        description: data?.description ?? "",
        dealStageId: getId(data?.dealStageId) ?? getId(data?.dealStage?.id) ?? null,
        // Deal fields - use data which handles both structures
        companyId: getId(data?.agent?.id) ?? getId(data?.agentId) ?? getId(data?.agent_id) ?? null,
        // Lead fields - use leadData which handles both structures
        firstName: leadData?.firstName || "",
        lastName: leadData?.lastName || "",
        passportNo: leadData?.passportNo ?? leadData?.passportNumber ?? "",
        leadSourceId: getId(leadData?.leadSourceId) ?? getId(leadData?.leadSource?.id) ?? null,
        country: leadData?.country ?? "",
        nationality: leadData?.nationality ?? "",
        interest: getId(leadData?.interest) ?? null,
        major: getId(leadData?.majorId) ?? getId(leadData?.major) ?? null,
        universityIds: getIdsArray(leadData?.universities) ?? [],
        semesterRegistered: getId(leadData?.semesterRegistered) ?? "",
        gender: leadData?.gender ?? null,
        contactMobile: data?.contactMobile || "",
        contactPhone: data?.contactPhone || "",
        agent_commission_paid: data?.agentCommissionPaid ?? data?.agent_commission_paid ?? "no",
        degree: data?.degree ?? null,
        uploadStatus: data?.uploadStatus ?? "pending",
        priority: data?.priority ?? "Direct",
      };

      form.setFieldsValue(formValues);
      setOriginalStageId(getId(data?.dealStageId) ?? getId(data?.dealStage?.id) ?? null);
    }
  }, [form, data, ownerList, dealStages, contactStatuses, universities, majors, interests]);

  const handleStageChange = (newStageId) => {
    // NOTE: In AntD, the form field is updated BEFORE onChange fires,
    // so we CANNOT use form.getFieldValue here — it already has the new value.
    // We use originalStageId (last successfully saved stage) as the reference.
    const currentSavedStageId = originalStageId;

    if (newStageId === currentSavedStageId) {
      return; // No actual change
    }

    const currentStageIndex = dealStages?.findIndex((s) => getId(s.id) === currentSavedStageId) ?? -1;
    const targetStageIndex = dealStages?.findIndex((s) => getId(s.id) === newStageId) ?? -1;
    const isMovingForward = targetStageIndex > currentStageIndex;

    if (isMovingForward) {
      const targetStage = dealStages[targetStageIndex];

      // 1. Sequential movement only — revert dropdown and warn
      if (targetStage?.stageName !== "Closed Lost" && targetStageIndex > currentStageIndex + 1) {
        message.warning(t("deal.validate.sequential"));
        form.setFieldsValue({ dealStageId: currentSavedStageId });
        return;
      }

      const attachments = data?.attachments || [];

      const checkKeyword = (keywords) => {
        return attachments.some((att) => {
          const name = (att.attachmentName || "").toLowerCase();
          const type = (att.attachmentType || "").toLowerCase();
          return keywords.some((kw) => name.includes(kw) || type.includes(kw));
        });
      };

      if (targetStage?.stageName === "Paper Submitted") {
        const hasPassport = checkKeyword(["passport", "جواز"]);
        const hasCertificate = checkKeyword(["certificate", "شهادة", "transcript", "بيان", "diploma", "وثيقة"]);
        if (!hasPassport || !hasCertificate) {
          message.warning(t("deal.validate.papersRequired"));
          form.setFieldsValue({ dealStageId: currentSavedStageId });
          return;
        }
      }

      if (targetStage?.stageName === "Conditional Acceptance Sent") {
        const hasConditional = checkKeyword(["conditional", "قبول مشروط", "القبول المشروط", "قبول_مشروط", "خطاب قبول مشروط", "خطاب_قبول_مشروط"]);
        if (!hasConditional) {
          message.warning(t("deal.validate.conditionalRequired"));
          form.setFieldsValue({ dealStageId: currentSavedStageId });
          return;
        }
      }

      if (targetStage?.stageName === "Payment on process") {
        const hasPayment = checkKeyword(["payment", "إيصال دفع", "إيصال الدفع", "دفع"]);
        if (!hasPayment) {
          message.warning(t("deal.validate.paymentRequired"));
          form.setFieldsValue({ dealStageId: currentSavedStageId });
          return;
        }
      }

      if (targetStage?.stageName === "Final letter sent") {
        const hasFinalLetter = checkKeyword(["final", "final letter", "final_letter", "خطاب نهائي", "نهائي"]);
        if (!hasFinalLetter) {
          message.warning(t("deal.validate.finalLetterRequired"));
          form.setFieldsValue({ dealStageId: currentSavedStageId });
          return;
        }
      }

      if (targetStage?.stageName === "Closed Won") {
        const hasStudentLetter = checkKeyword(["student letter", "student_letter", "خطاب الطالب", "خطاب_الطالب"]);
        if (!hasStudentLetter) {
          message.warning(t("deal.validate.studentLetterRequired"));
          form.setFieldsValue({ dealStageId: currentSavedStageId });
          return;
        }
      }
    }

    // Valid change — show notes modal
    setPendingStageId(newStageId);
    setStageNotes("");
    setStageModalVisible(true);
  };

  const handleStageChangeConfirm = async () => {
    if (!pendingStageId) return;

    const currentValues = form.getFieldsValue();
    currentValues.dealStageId = pendingStageId;

    // Save notes if provided, otherwise continue without notes
    if (stageNotes.trim()) {
      form.setFieldsValue({ ...currentValues, _stageNotes: stageNotes.trim() });
    } else {
      form.setFieldsValue(currentValues);
    }

    setStageModalVisible(false);
    setPendingStageId(null);
    setStageNotes("");

    // Trigger form submission
    form.submit();
  };

  const handleStageModalCancel = () => {
    // Revert dropdown to last saved stage
    form.setFieldsValue({ dealStageId: originalStageId });
    setStageModalVisible(false);
    setPendingStageId(null);
    setStageNotes("");
  };

  const handleSemesterFormSubmit = async () => {
    try {
      const values = await semesterForm.validateFields();
      const res = await addSemester(values).unwrap();
      if (res && res.id) {
        form.setFieldsValue({ semesterRegistered: res.id });
        setSemesterModalOpen(false);
        semesterForm.resetFields();
      }
    } catch (err) {
      console.error("Failed to add semester:", err);
    }
  };

  const onFinish = async (values) => {
    try {
      // Update Deal fields
      const dealValues = {
        dealName: `${values.firstName} ${values.lastName || ""}`.trim(),
        firstName: values.firstName,
        lastName: values.lastName,
        dealOwnerId: values.dealOwnerId && !isNaN(parseInt(values.dealOwnerId)) ? parseInt(values.dealOwnerId) : null,
        contactStatusId: values.contactStatusId && !isNaN(parseInt(values.contactStatusId)) ? parseInt(values.contactStatusId) : null,
        scholarship: typeof values.scholarship === "number" ? values.scholarship : values.scholarship && !isNaN(parseInt(values.scholarship)) ? parseInt(values.scholarship) : 0,
        description: values.description || "",
        dealStageId: values.dealStageId && !isNaN(parseInt(values.dealStageId)) ? parseInt(values.dealStageId) : null,
        agent_commission_paid: values.agent_commission_paid || "no",
        degree: values.degree || null,
        agent_id: values.companyId && !isNaN(parseInt(values.companyId)) ? parseInt(values.companyId) : null,
        uploadStatus: values.uploadStatus || "pending",
        priority: values.priority || "Direct",
        passportNumber: values.passportNo || "",
        nationality: values.nationality || "",
        gender: values.gender || null,
        country: values.country || "",
        interest: values.interest && !isNaN(parseInt(values.interest)) ? parseInt(values.interest) : null,
        majorId: values.major && !isNaN(parseInt(values.major)) ? parseInt(values.major) : null,
        semester_registered: values.semesterRegistered || "",
        universityIds: Array.isArray(values.universityIds) ? values.universityIds.map((id) => parseInt(id)) : [],
        contactMobile: values.contactMobile || "",
        contactPhone: values.contactPhone || "",
        mobile: values.contactMobile || "", // Fallback for backend
        phone: values.contactPhone || "", // Fallback for backend
      };

      // Add stage notes if provided
      if (values._stageNotes) {
        dealValues.stageNotes = values._stageNotes;
      }

      await updateDeal({ id, values: dealValues }).unwrap();

      // Update the saved stage reference so subsequent changes validate correctly
      if (dealValues.dealStageId) {
        setOriginalStageId(dealValues.dealStageId);
      }

      // Update Lead fields if lead exists
      const leadId = data?.lead?.id || data?.leadId || data?.lead_id;

      if (leadId) {
        const leadValues = {
          firstName: values.firstName,
          lastName: values.lastName,
          passportNo: values.passportNo || "",
          leadSourceId: values.leadSourceId && !isNaN(parseInt(values.leadSourceId)) ? parseInt(values.leadSourceId) : null,
          country: values.country || "",
          nationality: values.nationality || "",
          interest: values.interest && !isNaN(parseInt(values.interest)) ? parseInt(values.interest) : null,
          major: values.major && !isNaN(parseInt(values.major)) ? parseInt(values.major) : null,
          universityIds: Array.isArray(values.universityIds) ? values.universityIds.map(id => parseInt(id)) : [],
          semesterRegistered: values.semesterRegistered || "",
          gender: values.gender || null,
          contactMobile: values.contactMobile || "",
          contactPhone: values.contactPhone || "",
          mobile: values.contactMobile || "", // Fallback for backend
          phone: values.contactPhone || "",   // Fallback for backend
        };


        // Check if any lead fields actually changed
        const currentLeadData = data?.lead || data;
        const hasLeadChanges =
          leadValues.firstName !== (currentLeadData?.firstName || "") ||
          leadValues.lastName !== (currentLeadData?.lastName || "") ||
          leadValues.passportNo !== (currentLeadData?.passportNo || "") ||
          leadValues.country !== (currentLeadData?.country || "") ||
          leadValues.nationality !== (currentLeadData?.nationality || "") ||
          String(leadValues.semesterRegistered || "") !== String(currentLeadData?.semesterRegistered || "") ||
          leadValues.gender !== (currentLeadData?.gender || null) ||
          leadValues.contactMobile !== (currentLeadData?.contactMobile || "") ||
          leadValues.contactPhone !== (currentLeadData?.contactPhone || "") ||
          leadValues.leadSourceId !== (getId(currentLeadData?.leadSourceId) ?? getId(currentLeadData?.leadSource?.id) ?? null) ||
          leadValues.interest !== (getId(currentLeadData?.interest) ?? null) ||
          leadValues.major !== (getId(currentLeadData?.majorId) ?? getId(currentLeadData?.major) ?? null) ||
          JSON.stringify(leadValues.universityIds) !== JSON.stringify(getIdsArray(currentLeadData?.universities) ?? []);

        if (hasLeadChanges) {
          try {
            await updateLead({ id: leadId, values: leadValues }).unwrap();
          } catch (leadError) {
            console.log("Ignored lead update error (lead may have been deleted on conversion): ", leadError);
          }
        }
      }

      // Update Contact fields if contact exists
      const contactId = data?.contact?.id || data?.contactId || data?.contact_id;
      if (contactId) {
        const contactValues = {
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.contactPhone || "",
          mobile: values.contactMobile || "",
          email: data?.contact?.email || "",
        };
        dispatch(updateContact({ id: contactId, values: contactValues }));
      }

      // Refetch data to update the form with saved values
      // The useEffect will automatically update the form when data changes
      if (onUpdate) {
        const result = await onUpdate();
        // If refetch returns data, update form immediately
        if (result?.data) {
          const updatedData = result.data;
          const leadData = updatedData?.lead || updatedData;

          const formValues = {
            // Deal fields
            dealName: updatedData?.dealName ?? "",
            dealOwnerId: getId(updatedData?.dealOwnerId) ?? getId(updatedData?.dealOwner?.id) ?? null,
            contactStatusId: getId(updatedData?.contactStatusId) ?? getId(updatedData?.contactStatus?.id) ?? null,
            scholarship: updatedData?.scholarship !== undefined && updatedData?.scholarship !== null ? updatedData.scholarship : 0,
            description: updatedData?.description ?? "",
            dealStageId: getId(updatedData?.dealStageId) ?? getId(updatedData?.dealStage?.id) ?? null,
            // Lead fields
            firstName: leadData?.firstName || "",
            lastName: leadData?.lastName || "",
            passportNo: leadData?.passportNo ?? leadData?.passportNumber ?? "",
            leadSourceId: getId(leadData?.leadSourceId) ?? getId(leadData?.leadSource?.id) ?? null,
            country: leadData?.country ?? "",
            nationality: leadData?.nationality ?? "",
            interest: getId(leadData?.interest) ?? null,
            major: getId(leadData?.major) ?? null,
            universityIds: getIdsArray(leadData?.universities) ?? [],
            companyId: getId(updatedData?.agent?.id) ?? getId(updatedData?.agentId) ?? getId(updatedData?.agent_id) ?? null,
            semesterRegistered: getId(leadData?.semesterRegistered) ?? "",
            gender: leadData?.gender ?? null,
            contactMobile: updatedData?.contactMobile || "",
            contactPhone: updatedData?.contactPhone || "",
            agent_commission_paid: updatedData?.agentCommissionPaid ?? updatedData?.agent_commission_paid ?? "no",
            degree: updatedData?.degree ?? null,
            uploadStatus: updatedData?.uploadStatus ?? "pending",
            priority: updatedData?.priority ?? "Direct",
          };

          form.setFieldsValue(formValues);
        }
      }
    } catch (error) {
      // dealApi.onQueryStarted already shows a toast for API errors.
      // Log here for debugging only - no duplicate toast.
      console.error("onFinish error:", error);
    }
  };

  const isUpdating = isUpdatingDeal || isUpdatingLead;

  if (loading) {
    return <Loader />;
  }

  return (
    <Card className="border-none">
      <Form
        form={form}
        colon={false}
        disabled={!canEdit}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          scholarship: 0,
        }}>
        <div className="p-5">
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-4">{t("deal.dealInformation")}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label={t("deal.dealName")}
                name="dealName"
                hidden
                rules={[{ required: true, message: t("deal.dealNameRequired") }]}>
                <Input placeholder={t("deal.dealName")} />
              </Form.Item>

              <Form.Item
                label={t("common.description")}
                name="description"
                className="md:col-span-2"
                rules={[{ required: isRequired("description"), message: t("form.required") || "Required" }]}>
                <TextArea
                  rows={4}
                  placeholder={t("deal.descriptionPlaceholder") || "Enter description"}
                />
              </Form.Item>

              <Form.Item
                label={t("deal.dealOwner")}
                name="dealOwnerId"
                rules={[{ required: isRequired("dealOwnerId"), message: t("deal.selectOwner") || "Select Deal Owner" }]}>
                <Select
                  key={ownerLoading ? "loading" : "ready"}
                  style={{ width: "100%" }}
                  loading={ownerLoading}
                  showSearch
                  placeholder={t("deal.selectOwner") || "Select Deal Owner"}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={(() => {
                    const options = [...(ownerList || [])];
                    const cId = getId(data?.dealOwnerId) || getId(data?.dealOwner?.id) || (typeof data?.dealOwner !== 'object' ? getId(data?.dealOwner) : null);
                    let cName = data?.ownerFullName || (data?.dealOwner && typeof data?.dealOwner === 'object' ? (data.dealOwner.username || `${data.dealOwner.firstName || ''} ${data.dealOwner.lastName || ''}`.trim()) : null);

                    // If the owner is the current user and we don't have a name from the deal, use the name from localStorage
                    if (getId(cId) === getId(userId) && !cName) {
                      cName = userName;
                    }

                    if (cId && !options.some(item => getId(item.id) === cId)) {
                      options.push({ id: cId, username: cName || `User ${cId}`, firstName: '', lastName: '' });
                    }
                    return options.map((item) => ({
                      value: getId(item.id),
                      label: item?.username || (item.firstName || item.lastName ? `${item.firstName || ''} ${item.lastName || ''}`.trim() : null) || `User ${item.id}`,
                    }));
                  })()}
                />
              </Form.Item>

              <Form.Item
                label={t("lead.firstName")}
                name="firstName"
                rules={[{ required: isRequired("firstName"), message: t("lead.firstNameRequired") || "Required" }]}>
                <Input placeholder={t("lead.firstName")} />
              </Form.Item>

              <Form.Item
                label={t("lead.lastName")}
                name="lastName"
                rules={[{ required: isRequired("lastName"), message: t("lead.lastNameRequired") || "Required" }]}>
                <Input placeholder={t("lead.lastName")} />
              </Form.Item>

              <Form.Item
                label={t("common.phone")}
                name="contactPhone"
                normalize={(value) => value.replace(/[^0-9+]/g, "")}
                rules={[{ required: isRequired("phone"), message: t("form.required") || "Required" }, { pattern: /^\+?[0-9]+$/, message: t("common.numericPlusOnly") }]}>
                <div className="flex items-center gap-2">
                  <Input placeholder="+1 (273) 642-1785" style={{ width: "100%" }} value={watchedContactPhone} />
                  {watchedContactPhone && (
                    <a
                      href={`https://wa.me/${watchedContactPhone.toString().replace(/[^0-9+]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center min-w-[40px] h-10 rounded-lg bg-green-500 hover:bg-green-600 text-white transition-colors"
                      title={t("common.openInWhatsapp")}>
                      <FaWhatsapp className="text-lg" />
                    </a>
                  )}
                </div>
              </Form.Item>

              <Form.Item
                label={t("common.mobileWhatsApp")}
                name="contactMobile"
                normalize={(value) => value.replace(/[^0-9+]/g, "")}
                rules={[{ required: isRequired("mobile"), message: t("form.required") || "Required" }, { pattern: /^\+?[0-9]+$/, message: t("common.numericPlusOnly") }]}>
                <div className="flex items-center gap-2">
                  <Input placeholder="+1 (273) 642-1785" style={{ width: "100%" }} value={watchedContactMobile} />
                  {watchedContactMobile && (
                    <a
                      href={`https://wa.me/${watchedContactMobile.toString().replace(/[^0-9+]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center min-w-[40px] h-10 rounded-lg bg-green-500 hover:bg-green-600 text-white transition-colors"
                      title={t("common.openInWhatsapp")}>
                      <FaWhatsapp className="text-lg" />
                    </a>
                  )}
                </div>
              </Form.Item>

              {/* <Form.Item label={t("deal.contact")}>
                <Input
                  value={data?.contact ? `${data.contact.firstName || ''} ${data.contact.lastName || ''}` : ""}
                  disabled
                />
              </Form.Item> */}

              <Form.Item
                label={t("deal.stage")}
                name="dealStageId">
                <Select
                  key={stagesLoading ? "loading" : "ready"}
                  style={{ width: "100%" }}
                  loading={stagesLoading}
                  allowClear
                  showSearch
                  placeholder={t("deal.selectStage") || "Select Stage"}
                  onChange={handleStageChange}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={(() => {
                    const options = [...(dealStages || [])];
                    const cId = getId(data?.dealStageId) || getId(data?.dealStage?.id) || (typeof data?.dealStage !== 'object' ? getId(data?.dealStage) : null);
                    const cName = data?.stageName || (data?.dealStage && typeof data?.dealStage === 'object' ? data.dealStage.stageName : null);

                    if (cId && !options.some(item => getId(item.id) === cId)) {
                      options.push({ id: cId, stageName: cName || `Stage ${cId}` });
                    }
                    return options.map((item) => ({
                      value: getId(item.id),
                      label: item.stageName,
                    }));
                  })()}
                />
              </Form.Item>

              <Form.Item label={t("deal.probability")}>
                <Input
                  value={`${data?.probability || 0}%`}
                  disabled
                />
              </Form.Item>

              <Form.Item
                label={t("deal.passportNo")}
                name="passportNo"
                rules={[{ required: isRequired("passportNumber"), message: t("form.required") || "Required" }]}>
                <Input placeholder={t("deal.passportNoPlaceholder")} />
              </Form.Item>

              {/* <Form.Item
                label={t("deal.dealSource") || "Deal Source"}
                name="leadSourceId">
                <Select
                  style={{ width: "100%" }}
                  loading={leadSourceLoading}
                  allowClear
                  showSearch
                  placeholder={t("deal.selectLeadSource")}>
                  {leadSources?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item> */}

              <Form.Item
                label={t("common.country")}
                name="country">
                <Select
                  style={{ width: "100%" }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  placeholder={t("deal.selectCountry")}>
                  {countries.map((item) => (
                    <Option key={item.value} value={item.value}>
                      {item.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={t("common.nationality")}
                name="nationality"
                rules={[{ required: isRequired("nationality"), message: t("form.required") || "Required" }]}>
                <Select
                  style={{ width: "100%" }}
                  allowClear
                  showSearch
                  placeholder={t("deal.selectNationality")}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  options={nationalities}
                />
              </Form.Item>

              <Form.Item
                label={t("application.gender")}
                name="gender"
                rules={[{ required: isRequired("gender"), message: t("form.required") || "Required" }]}>
                <Select placeholder={t("application.selectGender")} allowClear>
                  <Option value="Male">{t("application.male")}</Option>
                  <Option value="Female">{t("application.female")}</Option>
                </Select>
              </Form.Item>

              <Form.Item
                label={t("lead.interest")}
                name="interest"
                rules={[{ required: isRequired("interest"), message: t("form.required") || "Required" }]}>
                <Select
                  style={{ width: "100%" }}
                  loading={interestLoading}
                  allowClear
                  showSearch
                  placeholder={t("deal.selectInterest")}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={interests?.map((item) => ({
                    value: getId(item.id),
                    label: item.name,
                  }))}
                />
              </Form.Item>

              <div className="flex flex-col">
                <Form.Item
                  label={t("university.universities")}
                  name="universityIds"
                  style={{ marginBottom: watchedUniversityIds?.length > 0 ? '8px' : '24px' }}
                  rules={[{ required: isRequired("universityIds"), message: t("form.required") || "Required" }]}>
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    loading={universityLoading}
                    allowClear
                    showSearch
                    placeholder={t("deal.selectUniversities")}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    tagRender={() => <span style={{ display: 'none' }} />}
                    options={universities?.map((item) => ({
                      value: getId(item.id),
                      label: item.universityName || item.name,
                    }))}
                  />
                </Form.Item>
                {watchedUniversityIds?.length > 0 && (
                  <div className="flex flex-col gap-2 mb-4">
                    {watchedUniversityIds.map(uid => {
                      const uni = universities?.find(u => getId(u.id) === uid);
                      if (!uni) return null;
                      return (
                        <div key={uid} className="flex justify-between items-center bg-blue-50/50 border border-blue-100 px-3 py-2 rounded-md">
                          <span className="text-sm break-words whitespace-normal leading-tight text-gray-700" title={uni.universityName || uni.name}>
                            {uni.universityName || uni.name}
                          </span>
                          <button
                            type="button"
                            className="text-gray-400 hover:text-red-500 cursor-pointer p-1 ml-2 shrink-0 transition-colors"
                            onClick={(e) => {
                              e.preventDefault();
                              const newIds = watchedUniversityIds.filter(id => id !== uid);
                              form.setFieldsValue({ universityIds: newIds });
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <Form.Item
                label={t("major.title")}
                name="major"
                rules={[{ required: isRequired("majorId"), message: t("form.required") || "Required" }]}>
                <Select
                  style={{ width: "100%" }}
                  loading={majorLoading}
                  allowClear
                  showSearch
                  placeholder={t("deal.selectMajor")}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={majors?.map((item) => ({
                    value: getId(item.id),
                    label: item.name,
                  }))}
                />
              </Form.Item>

              {/* <Form.Item
                label={t("company.companyName")}
                name="companyId">
                <Select
                  style={{ width: "100%" }}
                  loading={companyLoading}
                  allowClear
                  showSearch
                  placeholder={t("deal.selectAgent")}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }>
                  {companyList?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.companyName}
                    </Option>
                  ))}
                </Select>
              </Form.Item> */}

              <Form.Item
                label={t("deal.semesterRegistered")}
                required={isRequired("semesterRegistered")}
                style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <Form.Item
                    name="semesterRegistered"
                    noStyle
                    rules={[{ required: isRequired("semesterRegistered"), message: t("form.required") || "Required" }]}>
                    <Select
                      style={{ flex: 1 }}
                      loading={semestersLoading}
                      allowClear
                      showSearch
                      placeholder={t("deal.semesterRegisteredPlaceholder") || "Select Semester"}
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={(() => {
                        const semesterOptions = [...semesters];
                        const currentSemesterVal = Form.useWatch("semesterRegistered", form);
                        if (currentSemesterVal) {
                          const exists = semesters.some(
                            (s) => getId(s.id) === currentSemesterVal || String(s.name) === String(currentSemesterVal)
                          );
                          if (!exists) {
                            semesterOptions.push({
                              id: currentSemesterVal,
                              name: data?.semesterRegisteredName || currentSemesterVal,
                            });
                          }
                        }
                        return semesterOptions.map((item) => ({
                          value: getId(item.id),
                          label: item.name,
                        }));
                      })()}
                    />
                  </Form.Item>

                </div>
              </Form.Item>

              <Form.Item
                label={t("deal.contactStatus")}
                name="contactStatusId">
                <Select
                  style={{ width: "100%" }}
                  allowClear
                  showSearch
                  placeholder={t("deal.selectContactStatus")}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={(() => {
                    const options = [...(contactStatuses || [])];
                    const cId = getId(data?.contactStatusId) || getId(data?.contactStatus?.id) || (typeof data?.contactStatus !== 'object' ? getId(data?.contactStatus) : null);
                    const cName = data?.statusName || (data?.contactStatus && typeof data?.contactStatus === 'object' ? data.contactStatus.statusName : null);

                    if (cId && !options.some(item => getId(item.id) === cId)) {
                      options.push({ id: cId, statusName: cName || `Status ${cId}` });
                    }
                    return options.map((item) => ({
                      value: getId(item.id),
                      label: item.statusName,
                    }));
                  })()}
                />
              </Form.Item>

              <Form.Item
                label={t("deal.scholarship")}
                name="scholarship"
                rules={[{ required: isRequired("scholarship"), message: t("form.required") || "Required" }]}>
                <Select
                  style={{ width: "100%" }}
                  placeholder={t("deal.selectScholarship")}
                  allowClear>
                  <Option value={0}>0%</Option>
                  <Option value={25}>25%</Option>
                  <Option value={50}>50%</Option>
                  <Option value={100}>100%</Option>
                </Select>
              </Form.Item>


              {canReadCommission && (
                <Form.Item
                  label={t("deal.agentCommissionPaid")}
                  name="agent_commission_paid">
                  <Select placeholder={t("deal.selectStatus")} allowClear>
                    <Option value="no">{t("common.no")}</Option>
                    <Option value="yes">{t("common.yes")}</Option>
                  </Select>
                </Form.Item>
              )}

              <Form.Item
                label={t("deal.degree") || "Degree"}
                name="degree"
                rules={[{ required: isRequired("degree"), message: t("form.required") || "Required" }]}>
                <Select
                  placeholder={t("deal.selectDegree")}
                  loading={degreeLoading}
                  allowClear
                  showSearch>
                  {degrees?.map((item) => (
                    <Option key={item.id} value={item.name}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label={t("deal.uploadStatus")}
                name="uploadStatus"
                rules={[{ required: isRequired("uploadStatus"), message: t("form.required") || "Required" }]}>
                <Select placeholder={t("deal.uploadStatus")} allowClear>
                  <Option value="pending">{t("deal.pending")}</Option>
                  <Option value="done">{t("deal.done")}</Option>
                </Select>
              </Form.Item>
              <Form.Item
                label={t("deal.priority")}
                name="priority"
                rules={[{ required: isRequired("priority"), message: t("form.required") || "Required" }]}>
                <Select placeholder={t("deal.selectPriority")} allowClear>
                  <Option value="Direct">{t("deal.direct")}</Option>
                  <Option value="Transfer">{t("deal.transfer")}</Option>
                </Select>
              </Form.Item>
            </div>


            {canEdit && (
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={isUpdating}>
                  {t("deal.saveChanges")}
                </Button>
              </Form.Item>
            )}
          </div>


        </div>
      </Form>

      {/* Modal for Stage Change Notes */}
      <Modal
        title={t("deal.changeStageTitle")}
        open={stageModalVisible}
        onCancel={handleStageModalCancel}
        footer={[
          <Button key="cancel" onClick={handleStageModalCancel}>
            {t("common.cancel")}
          </Button>,
          <Button key="continue" type="primary" onClick={handleStageChangeConfirm} loading={isUpdatingDeal}>
            {t("deal.continue")}
          </Button>,
        ]}
        width={600}
      >
        <div className="space-y-4">
          <div>
            <p className="text-gray-700 dark:text-gray-300 mb-2">
              {t("deal.changeStageDescription")}
            </p>
            <TextArea
              rows={4}
              placeholder={t("deal.changeStagePlaceholder")}
              value={stageNotes}
              onChange={(e) => setStageNotes(e.target.value)}
              maxLength={500}
              showCount
              className="mb-4"
            />
          </div>
        </div>
      </Modal>

      {/* Modal for Quick Add Semester Registered */}
      <Modal
        title={t("semesterRegistered.createSemester") || "Create Semester"}
        open={semesterModalOpen}
        onCancel={() => {
          setSemesterModalOpen(false);
          semesterForm.resetFields();
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setSemesterModalOpen(false);
            semesterForm.resetFields();
          }}>
            {t("common.cancel") || "Cancel"}
          </Button>,
          <Button key="submit" type="primary" onClick={handleSemesterFormSubmit} loading={isAddingSemester}>
            {t("common.save") || "Save"}
          </Button>,
        ]}
      >
        <Form form={semesterForm} layout="vertical">
          <Form.Item
            label={t("semesterRegistered.name") || "Semester Name"}
            name="name"
            rules={[{ required: true, message: t("semesterRegistered.nameRequired") || "Required" }]}
          >
            <Input placeholder={t("semesterRegistered.name") || "Semester Name"} />
          </Form.Item>
        </Form>
      </Modal>
    </Card >
  );
}

